import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from './ui/Modal';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { AlertMessage } from './ui/AlertMessage';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

/** Party types that have challan details (GST No + Broker). Must match backend PartyService. */
const CHALLAN_PARTY_CATEGORIES = new Set([
  'BUTTA_CUTTING_PARTIES',
  'MILL_FINISH_PARTIES',
  'HANDWORK_PARTIES',
  'BORDER_CUTTING_PARTIES',
]);

/** Indian GSTIN, e.g. 24ABCDE1234F1Z5 (same rule as the backend). */
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const normalizeGstNo = (value) => String(value || '').replace(/\s+/g, '').toUpperCase();

export const getInitialPartyForm = () => ({
  name: '',
  contact: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  gstNo: '',
  brokerName: '',
});

export function mapPartyRecordToForm(record) {
  if (!record) return getInitialPartyForm();
  return {
    name: record.name || '',
    contact: record.contact || '',
    addressLine1: record.addressLine1 || '',
    addressLine2: record.addressLine2 || '',
    city: record.city || '',
    state: record.state || '',
    gstNo: record.gstNo || '',
    brokerName: record.brokerName || '',
  };
}

const PARTY_MODAL_ICON = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

/**
 * Shared create/edit party modal — same fields and behaviour as PartyMaster.
 */
export function PartyFormModal({
  open,
  onClose,
  mode = 'create',
  partyCategory,
  modalTitle,
  namePlaceholder = 'Name',
  record = null,
  onSaved,
  formId = 'party-form',
}) {
  const [form, setForm] = useState(getInitialPartyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const showChallanFields = CHALLAN_PARTY_CATEGORIES.has(partyCategory);

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    const h = { 'Content-Type': 'application/json' };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, []);

  const resetForm = useCallback(() => {
    setForm(mapPartyRecordToForm(mode === 'edit' ? record : null));
    setFieldErrors({});
    setFormError('');
  }, [mode, record]);

  useEffect(() => {
    if (!open) return;
    resetForm();
  }, [open, resetForm]);

  useEffect(() => {
    if (!formError) return undefined;
    const timer = setTimeout(() => setFormError(''), 5000);
    return () => clearTimeout(timer);
  }, [formError]);

  const updateField = (field, value) => {
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.name.trim()) {
      nextErrors.name = 'Please enter name';
    }
    if (showChallanFields) {
      if (!form.brokerName.trim()) {
        nextErrors.brokerName = 'Please enter broker';
      }
      const gst = normalizeGstNo(form.gstNo);
      if (gst && !GSTIN_PATTERN.test(gst)) {
        nextErrors.gstNo = 'Please enter a valid 15-character GST No (e.g. 24ABCDE1234F1Z5)';
      }
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        category: partyCategory,
        name: form.name.trim(),
        contact: form.contact.trim() || null,
        addressLine1: form.addressLine1.trim() || null,
        addressLine2: form.addressLine2.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        gstNo: showChallanFields ? normalizeGstNo(form.gstNo) || null : null,
        brokerName: showChallanFields ? form.brokerName.trim() || null : null,
      };
      const isCreate = mode === 'create';
      const response = await fetch(
        isCreate ? `${API_BASE_URL}/api/parties` : `${API_BASE_URL}/api/parties/${record.id}`,
        {
          method: isCreate ? 'POST' : 'PUT',
          headers,
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || errData?.message || 'Unable to save details');
      }
      const saved = await response.json();
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setFormError(err?.message || 'Unable to save details');
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'create' ? modalTitle : `Edit ${String(modalTitle).replace(/^Add\s+/, '')}`;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="xl"
      title={title}
      description={mode === 'create' ? 'Enter details and save.' : 'Update this record and save your changes.'}
      icon={PARTY_MODAL_ICON}
      footer={(
        <>
          <Button type="button" variant="secondary" onClick={resetForm} disabled={saving}>
            Reset
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={saving} disabled={saving}>
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </>
      )}
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="py-6 space-y-5">
        <AlertMessage message={formError} type="error" onClose={() => setFormError('')} />

        <Input
          label="Name *"
          placeholder={namePlaceholder}
          value={form.name}
          onChange={(e) => updateField('name', e.target.value)}
          error={fieldErrors.name}
          required
        />

        {showChallanFields && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="Broker *"
              placeholder="Broker name (for challan)"
              value={form.brokerName}
              onChange={(e) => updateField('brokerName', e.target.value)}
              error={fieldErrors.brokerName}
              maxLength={120}
              required
            />
            <Input
              label="GST No"
              placeholder="e.g. 24ABCDE1234F1Z5"
              value={form.gstNo}
              onChange={(e) => updateField('gstNo', e.target.value.toUpperCase())}
              error={fieldErrors.gstNo}
              maxLength={15}
              hint="Optional — 15 characters."
            />
          </div>
        )}

        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Address</p>
          <div className="grid grid-cols-1 gap-3">
            <Input
              label="Line 1"
              placeholder="Street, building, area…"
              value={form.addressLine1}
              onChange={(e) => updateField('addressLine1', e.target.value)}
            />
            <Input
              label="Line 2"
              placeholder="Additional line (optional)"
              value={form.addressLine2}
              onChange={(e) => updateField('addressLine2', e.target.value)}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="City"
                placeholder="City"
                value={form.city}
                onChange={(e) => updateField('city', e.target.value)}
              />
              <Input
                label="State"
                placeholder="State"
                value={form.state}
                onChange={(e) => updateField('state', e.target.value)}
              />
            </div>
          </div>
        </div>

        <Input
          label="Contact"
          placeholder="Phone, WhatsApp, email…"
          value={form.contact}
          onChange={(e) => updateField('contact', e.target.value)}
          hint="Optional — any contact you use for this party."
        />
      </form>
    </Modal>
  );
}
